/**
 * Romanian Bank IBAN Prefix Mapping
 *
 * IBAN Structure for Romania: RO + 2 check digits + 4 letter bank code + 16 digits
 * The 4-letter code (positions 5-8) identifies the bank.
 *
 * Sources:
 * - https://www.swift-code.com/romania
 * - https://bank-code.net/country/ROMANIA-(RO).html
 */

const ROMANIAN_BANK_CODES = {
  // Major Banks
  'BTRL': 'Banca Transilvania',
  'RNCB': 'Banca Comercială Română (BCR)',
  'BRDE': 'BRD - Groupe Societe Generale',
  'BACX': 'UniCredit Bank',
  'INGB': 'ING Bank',
  'RZBR': 'Raiffeisen Bank',
  'PIRB': 'First Bank (ex Piraeus)',
  'BPOS': 'Banca Românească',
  'EGNA': 'Banca Naţională a României (BNR)',
  'BUCU': 'Alpha Bank România',
  'CECE': 'CEC Bank',
  'OTPV': 'OTP Bank România',
  'TREZ': 'Trezoreria Statului',
  'EXIM': 'EximBank',
  'CARP': 'Patria Bank (ex Carpatica)',
  'DAFB': 'Banca de Export-Import a României',
  'FNNB': 'First Bank',
  'MIND': 'Idea Bank (ex Marfin)',
  'NBOR': 'Banca Națională a României',
  'PORL': 'Porsche Bank România',
  'UGBI': 'Garanti BBVA',
  'VBBU': 'Vista Bank',
  'WBAN': 'Libra Internet Bank',
  'CRCO': 'Creditcoop',
  'DARO': 'Banca de Export Import',
  'MILB': 'Millennium Bank',
  'BREL': 'Libra Internet Bank',
  'BTRN': 'Banca Transilvania',
  'ROIN': 'Romanian International Bank',
  'CITI': 'Citibank România',
  'ABNR': 'ABN AMRO Bank România',
  'BNRB': 'BNR',

  // Credit Cooperatives and Others
  'CCRE': 'Credit Agricol',
  'FRBU': 'Credit Europe Bank',
  'HVBL': 'Hypovereinsbank',
  'IORT': 'Intesa Sanpaolo Bank',
  'BCRL': 'Banca Centrală Cooperatistă',
  'BITR': 'Banca Italo-Romena',
  'FNNR': 'Bank Leumi România',
  'MIRO': 'ProCredit Bank',
  'PNRB': 'Piraeus Bank',
  'RORX': 'Saxo Bank',
  'VBRO': 'Volksbank România',

  // Additional/Updated codes
  'BSEA': 'Banca de Scont',
  'CECR': 'CEC Bank',
  'REVO': 'Revolut',
  'TFBK': 'TBI Bank',
  'PAYX': 'PayU',
};

/**
 * Extract bank code from IBAN
 * Romanian IBAN: RO + 2 check digits + 4 letter bank code + 16 digits
 *
 * @param {string} iban - The IBAN string
 * @returns {string|null} - 4-letter bank code or null
 */
export function extractBankCode(iban) {
  if (!iban || typeof iban !== 'string') return null;

  // Clean IBAN: remove spaces and convert to uppercase
  const cleanIban = iban.replace(/\s/g, '').toUpperCase();

  // Romanian IBAN should be 24 characters: RO + 2 digits + 4 letters + 16 chars
  if (cleanIban.length < 8) return null;

  // Extract bank code (positions 5-8, 0-indexed: characters 4-7)
  const bankCode = cleanIban.substring(4, 8);

  // Verify it's letters only
  if (!/^[A-Z]{4}$/.test(bankCode)) return null;

  return bankCode;
}

/**
 * Get bank name from IBAN
 *
 * @param {string} iban - The IBAN string
 * @returns {string} - Bank name or empty string if not found
 */
export function getBankNameFromIban(iban) {
  const bankCode = extractBankCode(iban);
  if (!bankCode) return '';

  return ROMANIAN_BANK_CODES[bankCode] || '';
}

/**
 * Get bank name from an array of IBANs (uses the first one)
 *
 * @param {Array|string} ibans - Array of IBANs or single IBAN string
 * @returns {string} - Bank name from first IBAN or empty string
 */
export function getBankNameFromIbans(ibans) {
  if (!ibans) return '';

  // Handle array
  if (Array.isArray(ibans)) {
    if (ibans.length === 0) return '';
    return getBankNameFromIban(ibans[0]);
  }

  // Handle string
  if (typeof ibans === 'string') {
    return getBankNameFromIban(ibans);
  }

  return '';
}

/**
 * Get all bank codes and names (for debugging or dropdown)
 * @returns {Object} - All bank codes mapped to names
 */
export function getAllBankCodes() {
  return { ...ROMANIAN_BANK_CODES };
}

export default {
  extractBankCode,
  getBankNameFromIban,
  getBankNameFromIbans,
  getAllBankCodes,
  ROMANIAN_BANK_CODES,
};
